import React, { useEffect, useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout, getUser, canEditProducts } from '../utils/auth';
import api from '../utils/api';
import { Home, Package, ShoppingCart, TruckIcon, FileText, DollarSign, BarChart3, Trash2, LogOut, User, X, ClipboardList, Receipt, Calculator, Users, Database, Leaf, UserCheck, Wallet, Boxes, Ticket, AlertTriangle } from 'lucide-react';

export default function Sidebar({ isOpen, onClose, isMobile }) {
  const { t } = useTranslation();
  const location = useLocation();
  const user = getUser();

  // For field_team users we also show the list of assigned retailers below
  // the primary nav so they can jump straight into any retailer's view
  // (matches the dashboard's own hamburger menu). Clicking a retailer
  // navigates to /field-team/dashboard?retailer=<id>.
  const [fieldRetailers, setFieldRetailers] = useState([]);
  useEffect(() => {
    if (user?.role !== 'field_team') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/field-team/portfolio-summary');
        if (!cancelled) {
          setFieldRetailers(data?.retailer_summaries || []);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Pending ticket count on the sidebar Tickets link so admins/staff/field
  // see a nudge for tickets assigned to them that aren't done yet.
  const [pendingTicketCount, setPendingTicketCount] = useState(0);
  useEffect(() => {
    if (!user?.id || !['admin', 'staff', 'field_team'].includes(user?.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/tickets');
        if (cancelled) return;
        const count = (data || []).filter(
          (t) =>
            (t.assigned_to || []).includes(user.id) &&
            !['completed', 'cancelled'].includes(t.status)
        ).length;
        setPendingTicketCount(count);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, location.pathname]);
  
  // Admin has access to ALL tabs
  const adminLinks = [
    { path: '/admin/dashboard', icon: Home, labelKey: 'app.dashboard' },
    { path: '/admin/products', icon: Leaf, labelKey: 'app.products' },
    { path: '/admin/retail-plans', icon: Boxes, labelKey: 'app.retailPlans' },
    { path: '/admin/stock-status', icon: ClipboardList, labelKey: 'app.stockStatus' },
    { path: '/admin/procurement', icon: TruckIcon, labelKey: 'app.procurement' },
    { path: '/admin/quick-commerce', icon: ShoppingCart, labelKey: 'app.quickCommerce' },
    { path: '/admin/retailer-orders', icon: ShoppingCart, labelKey: 'app.retailerOrders' },
    { path: '/admin/wastage-dashboard', icon: Trash2, labelKey: 'app.wastageDashboard' },
    { path: '/admin/variable-expenses', icon: Receipt, labelKey: 'app.variableExpenses' },
    { path: '/admin/fixed-expenses', icon: Calculator, labelKey: 'app.fixedExpenses' },
    { path: '/admin/cashflow', icon: Wallet, labelKey: 'app.cashflow' },
    { path: '/admin/labor-costs', icon: UserCheck, labelKey: 'app.laborCosts' },
    { path: '/tickets', icon: Ticket, label: 'Tickets' },
    { path: '/admin/users', icon: Users, labelKey: 'app.userManagement' },
    { path: '/admin/duplicate-invoice-report', icon: AlertTriangle, label: 'Duplicate Invoice Report' },
    { path: '/admin/backup', icon: Database, labelKey: 'app.backup' },
  ];

  // Staff access: Procurement, Stock Status, Quick Commerce, Retailer Orders, Wastage Dashboard, Variable Expenses, Attendance
  // NOTE: Products tab is admin-only EXCEPT for the explicit allowlist in
  // canEditProducts (currently Shradha Salunke). The link below is appended
  // conditionally after the base staffLinks array.
  const staffLinks = [
    { path: '/admin/stock-status', icon: ClipboardList, labelKey: 'app.stockStatus' },
    { path: '/admin/procurement', icon: TruckIcon, labelKey: 'app.procurement' },
    { path: '/admin/quick-commerce', icon: ShoppingCart, labelKey: 'app.quickCommerce' },
    { path: '/admin/retailer-orders', icon: ShoppingCart, labelKey: 'app.retailerOrders' },
    { path: '/admin/wastage-dashboard', icon: Trash2, labelKey: 'app.wastageDashboard' },
    { path: '/admin/variable-expenses', icon: Receipt, labelKey: 'app.variableExpenses' },
    { path: '/tickets', icon: Ticket, label: 'Tickets' },
    { path: '/staff/attendance', icon: UserCheck, labelKey: 'app.attendance' },
  ];
  if (user?.role === 'staff' && canEditProducts(user)) {
    // Insert Products at the top so the allowlisted staff see it in the same
    // position as admins do.
    staffLinks.unshift({ path: '/admin/products', icon: Leaf, labelKey: 'app.products' });
  }

  // Retailer has their own portal (to be built)
  const retailerLinks = [
    { path: '/retailer/dashboard', icon: Home, labelKey: 'app.dashboard' },
  ];

  // Field team - dashboard + tickets/tasks
  const fieldTeamLinks = [
    { path: '/field-team/dashboard', icon: Home, labelKey: 'app.dashboard' },
    { path: '/tickets', icon: Ticket, label: 'My Tasks' },
  ];

  const links =
    user?.role === 'admin' ? adminLinks
    : user?.role === 'staff' ? staffLinks
    : user?.role === 'field_team' ? fieldTeamLinks
    : retailerLinks;

  // Close sidebar on every route change (no isMobile guard)
  useEffect(() => {
    if (onClose) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  // Nav click handler - close synchronously and unconditionally
  const handleNavClick = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Sidebar classes - hidden by default on mobile, shown only when opened
  const sidebarClasses = isMobile 
    ? `sidebar ${isOpen ? 'mobile-open' : ''}`
    : 'sidebar';

  return (
    <>
      {/* Overlay - show when drawer is open on mobile */}
      {isMobile && isOpen && (
        <div 
          className="sidebar-overlay active"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      
      {/* Sidebar */}
      <div className={sidebarClasses} data-testid="sidebar">
        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://customer-assets-lxgj4vgw.emergentagent.net/job_harvest-hub-384/artifacts/ouyg8w1d_vezlo_logo_PRINT_300dpi.webp" 
              alt="Vezlo Logo" 
              className="h-20 w-auto"
              style={{ borderRadius: '12px' }}
            />
          </div>
          {/* X close button - always visible for accessibility */}
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 rounded-lg"
            data-testid="close-sidebar-button"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>
        
        <nav className="p-4 overflow-y-auto flex-1" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          {links.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path;
            const linkLabel = link.labelKey ? t(link.labelKey) : link.label;
            const showBadge = link.path === '/tickets' && pendingTicketCount > 0;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${linkLabel.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg mb-1 hover:bg-gray-100 ${
                  isActive ? 'bg-[#14532D] text-white hover:bg-[#166534]' : 'text-gray-700'
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium flex-1">{linkLabel}</span>
                {showBadge && (
                  <span
                    className="min-w-[20px] h-[20px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
                    data-testid="sidebar-pending-ticket-badge"
                  >
                    {pendingTicketCount}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Field team: assigned retailers below the primary links so the
              menu doesn't feel bare when they're on the tickets page. */}
          {user?.role === 'field_team' && fieldRetailers.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                My Retailers
              </p>
              {fieldRetailers.map((r) => (
                <Link
                  key={r.retailer_id}
                  to={`/field-team/dashboard?retailer=${r.retailer_id}`}
                  onClick={handleNavClick}
                  data-testid={`sidebar-retailer-${r.retailer_id}`}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg mb-1 hover:bg-gray-100 text-gray-700"
                >
                  <ShoppingCart size={16} />
                  <span className="text-sm truncate">{r.retailer_name}</span>
                </Link>
              ))}
            </div>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-10 h-10 rounded-full bg-[#14532D] flex items-center justify-center text-white flex-shrink-0">
              <User size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100"
          >
            <LogOut size={16} />
            {t('app.logout')}
          </button>
        </div>
      </div>
    </>
  );
}
